/** First observed browser entry. No raw URL queries, fragments, or private paths persist. */
export const ACQUISITION_KEY = "stasht.signup-acquisition.v1";
export const CAMPAIGN_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
export type CampaignKey = typeof CAMPAIGN_KEYS[number];
export type SignupAcquisition = Partial<Record<CampaignKey, string>> & {
  version: 1;
  method: "web";
  captured_at: string;
  landing_page: string;
  referrer?: string;
  status: "campaign" | "referral" | "same_site" | "not_present" | "unavailable";
};
type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;

export function safeCampaign(value: unknown): string | undefined {
  return typeof value === "string" && /^[A-Za-z0-9_().-]{1,100}$/.test(value) ? value : undefined;
}

const PUBLIC_PATHS = new Set(["/", "/blog", "/download", "/login", "/signup", "/about", "/pricing", "/privacy", "/terms", "/faq", "/compare", "/web-landing", "/mobile-app-landing", "/app-to-save-and-organize-social-media-posts", "/save-places-from-tiktok", "/organize-saved-reels", "/save-tiktoks", "/save-recipes-from-instagram-and-tiktok", "/save-travel-ideas-from-instagram-and-tiktok", "/save-events-from-instagram-and-tiktok", "/save-gift-ideas-from-social-media", "/recime-alternative", "/stacklist-alternative"]);
export function safeLandingPage(href: string): string | undefined {
  try {
    const url = new URL(href);
    if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) return;
    const path = url.pathname.replace(/\/$/, "") || "/";
    const safePath = PUBLIC_PATHS.has(path) || /^\/blog\/[a-z0-9-]{1,100}$/.test(path) ? path : "/";
    return `${url.origin}${safePath}`;
  } catch { return; }
}

export function observedBrowserAcquisition(href: string, referrer: string, now = new Date()): SignupAcquisition | undefined {
  const landing_page = safeLandingPage(href);
  if (!landing_page) return;
  const url = new URL(href);
  const campaign: Partial<Record<CampaignKey, string>> = {};
  for (const key of CAMPAIGN_KEYS) {
    const value = safeCampaign(url.searchParams.get(key));
    if (value) campaign[key] = value;
  }
  let origin: string | undefined;
  let status: SignupAcquisition["status"] = referrer ? "unavailable" : "not_present";
  try {
    const source = new URL(referrer);
    if (["http:", "https:"].includes(source.protocol) && !source.username && !source.password) {
      origin = source.origin;
      const host = (value: string) => value.replace(/^www\./, "");
      status = host(source.hostname) === host(url.hostname) ? "same_site" : "referral";
    }
  } catch { /* Empty and unsupported referrers remain explicit observations. */ }
  if (Object.keys(campaign).length) status = "campaign";
  return { version: 1, method: "web", captured_at: now.toISOString(), landing_page, ...(origin ? { referrer: origin } : {}), ...campaign, status };
}

export function createAcquisitionCapture() {
  let firstTouch: SignupAcquisition | undefined;
  let persistence: Storage | undefined;
  return {
    capture(href: string, referrer: string, storage?: Storage, now?: Date) {
      if (firstTouch) return;
      persistence = storage;
      try {
        const saved = JSON.parse(storage?.getItem(ACQUISITION_KEY) || "null");
        if (saved?.version === 1 && saved.method === "web" && typeof saved.captured_at === "string" && Number.isFinite(Date.parse(saved.captured_at))) {
          // Reconstruct the allowlisted shape even if local storage was modified.
          const restored = new URL(saved.landing_page);
          for (const key of CAMPAIGN_KEYS) {
            const value = safeCampaign(saved[key]);
            if (value) restored.searchParams.set(key, value);
          }
          firstTouch = observedBrowserAcquisition(restored.href, typeof saved.referrer === "string" ? saved.referrer : "", new Date(saved.captured_at));
          if (firstTouch && saved.status === "unavailable" && firstTouch.status === "not_present") firstTouch.status = "unavailable";
        }
      } catch { /* Blocked/corrupt storage cannot interrupt entry or authentication. */ }
      firstTouch ??= observedBrowserAcquisition(href, referrer, now);
      if (firstTouch) {
        try { storage?.setItem(ACQUISITION_KEY, JSON.stringify(firstTouch)); } catch { /* Keep evidence in memory for this document. */ }
      }
    },
    get(): SignupAcquisition | undefined { return firstTouch ? { ...firstTouch } : undefined; },
    consume() {
      firstTouch = undefined;
      try { persistence?.removeItem(ACQUISITION_KEY); } catch { /* Failed storage removal must not affect successful auth. */ }
      persistence = undefined;
    },
  };
}

export const signupAcquisition = createAcquisitionCapture();

/** Both auth final-request paths use the same captured evidence, without changing credentials. */
export function withSignupAcquisition<T extends object>(request: T): T & { acquisition?: SignupAcquisition } {
  const acquisition = signupAcquisition.get();
  return acquisition ? { ...request, acquisition } : request;
}
